import type { Permission } from '@ingenico/react-section-utils';
import type { StandaloneUserType } from '../constants/userTypeSwitch.const';
import { USER_TYPES } from '../constants/userTypeSwitch.const';
import {
  OVERRIDABLE_PERMISSIONS,
  STANDALONE_PERMISSION_OVERRIDE_STORAGE_PREFIX,
  type PermissionOverrideValue,
  type PermissionOverrides,
} from '../constants/standalonePermissionOverrides.const';

const getOverrideStorageKey = (userType: StandaloneUserType): string =>
  `${STANDALONE_PERMISSION_OVERRIDE_STORAGE_PREFIX}:${userType}`;

const parseStoredOverrides = (serialized: string | null): PermissionOverrides => {
  if (!serialized) {
    return {};
  }

  try {
    const parsedValue = JSON.parse(serialized) as Record<string, unknown>;
    const validOverrides: PermissionOverrides = {};

    for (const permission of OVERRIDABLE_PERMISSIONS) {
      const value = parsedValue[permission];
      if (typeof value === 'boolean') {
        validOverrides[permission] = value;
      }
    }

    return validOverrides;
  } catch {
    return {};
  }
};

export abstract class StandalonePermissionOverridesService {
  private static readonly interceptedBasePermissionsByUserType: Partial<Record<StandaloneUserType, Permission[]>> = {};

  static setInterceptedBasePermissions(userType: StandaloneUserType, permissions: Permission[]): void {
    this.interceptedBasePermissionsByUserType[userType] = Array.from(new Set(permissions));
  }

  static getInterceptedBasePermissions(userType: StandaloneUserType): Permission[] {
    return this.interceptedBasePermissionsByUserType[userType] ?? [];
  }

  /**
   * Get all available device-management permissions that can be overridden.
   */
  static getAvailablePermissions(): readonly string[] {
    return OVERRIDABLE_PERMISSIONS;
  }

  /**
   * Get permission overrides for a specific user type from localStorage.
   */
  static getUserTypeOverrides(userType: StandaloneUserType): PermissionOverrides {
    try {
      return parseStoredOverrides(localStorage.getItem(getOverrideStorageKey(userType)));
    } catch {
      return {};
    }
  }

  /**
   * Set permission overrides for a specific user type.
   * Validates against available permissions and persists to localStorage.
   */
  static setUserTypeOverrides(userType: StandaloneUserType, overrides: PermissionOverrides): void {
    const sanitizedOverrides: PermissionOverrides = {};

    for (const permission of OVERRIDABLE_PERMISSIONS) {
      const value = overrides[permission];
      if (typeof value === 'boolean') {
        sanitizedOverrides[permission] = value;
      }
    }

    try {
      localStorage.setItem(getOverrideStorageKey(userType), JSON.stringify(sanitizedOverrides));
    } catch {
      // Ignore storage access failures.
    }
  }

  /**
   * Reset all permission overrides for a specific user type.
   */
  static resetUserTypeOverrides(userType: StandaloneUserType): void {
    try {
      localStorage.removeItem(getOverrideStorageKey(userType));
    } catch {
      // Ignore storage access failures.
    }
  }

  /**
   * Reset all permission overrides for all user types.
   */
  static resetAllOverrides(): void {
    for (const userType of Object.values(USER_TYPES)) {
      this.resetUserTypeOverrides(userType);
    }
  }

  /**
   * Apply permission overrides to a base permission array.
   * true forces availability; false forces removal.
   */
  static getResolvedPermissions(basePermissions: Permission[], userType: StandaloneUserType): Permission[] {
    const overrides = this.getUserTypeOverrides(userType);
    const result = [...basePermissions];

    for (const [permission, value] of Object.entries(overrides)) {
      if (value === true) {
        if (!result.includes(permission as Permission)) {
          result.push(permission as Permission);
        }
      } else if (value === false) {
        const index = result.indexOf(permission as Permission);
        if (index > -1) {
          result.splice(index, 1);
        }
      }
    }

    return result;
  }

  static getEffectivePermissionAvailabilityState(userType: StandaloneUserType): Record<string, boolean> {
    const basePermissions = this.getInterceptedBasePermissions(userType);
    const resolvedPermissions = this.getResolvedPermissions(basePermissions, userType);
    const resolvedPermissionSet = new Set(resolvedPermissions);
    const state: Record<string, boolean> = {};

    for (const permission of OVERRIDABLE_PERMISSIONS) {
      state[permission] = resolvedPermissionSet.has(permission as Permission);
    }

    return state;
  }

  static buildOverridesFromAvailabilityState(
    userType: StandaloneUserType,
    availabilityState: Record<string, boolean>,
  ): PermissionOverrides {
    const basePermissions = new Set(this.getInterceptedBasePermissions(userType));
    const overrides: PermissionOverrides = {};

    for (const permission of OVERRIDABLE_PERMISSIONS) {
      const desiredAvailability = availabilityState[permission] ?? false;
      const baseAvailability = basePermissions.has(permission as Permission);

      if (desiredAvailability !== baseAvailability) {
        overrides[permission] = desiredAvailability;
      }
    }

    return overrides;
  }

  /**
   * Get the current state of permission overrides as a UI-friendly record
   * indicating which permissions are currently overridden.
   */
  static getResolvedOverrideState(userType: StandaloneUserType): Record<string, PermissionOverrideValue | null> {
    const overrides = this.getUserTypeOverrides(userType);
    const state: Record<string, PermissionOverrideValue | null> = {};

    for (const permission of OVERRIDABLE_PERMISSIONS) {
      state[permission] = overrides[permission] ?? null;
    }

    return state;
  }
}
